import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Helmet } from '@dr.pogodin/react-helmet'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { Paper, Typography, TextField, Button, Alert as MuiAlert } from '@mui/material'

import UserApi from '../../api/UserApi'

const userApi = new UserApi()

const Wrapper = styled(Paper)`
    padding: ${(props) => props.theme.spacing(6)};

    ${(props) => props.theme.breakpoints.up('md')} {
        padding: ${(props) => props.theme.spacing(10)};
    }
`

const Alert = styled(MuiAlert)`
    margin-bottom: ${(props) => props.theme.spacing(2)};
`

function useQuery() {
    return new URLSearchParams(useLocation().search)
}

function ResetPasswordWithCode() {
    const { t } = useTranslation()
    const query = useQuery()
    const navigate = useNavigate()

    const emailRef = useRef<HTMLInputElement>(null)
    const codeRef = useRef<HTMLInputElement>(null)
    const newPasswordRef = useRef<HTMLInputElement>(null)
    const confirmPasswordRef = useRef<HTMLInputElement>(null)

    // State for showing message
    const [showMessage, setShowMessage] = useState(false)
    const [showForm, setShowForm] = useState(true)

    // Get email and code from query parameters
    const emailFromQuery = query.get('email') || ''
    const codeFromQuery = query.get('code') || ''

    useEffect(() => {
        if (query.get('showMessage') === 'true') {
            setShowMessage(true)
        }
    }, [query])

    // Focus on the first empty field
    useEffect(() => {
        if (!emailFromQuery) {
            emailRef.current?.focus()
        } else if (!codeFromQuery) {
            codeRef.current?.focus()
        } else {
            newPasswordRef.current?.focus()
        }
    }, [emailFromQuery, codeFromQuery])

    const handleReRequestCode = async () => {
        try {
            // Trigger re-requesting the password reset code
            await userApi.requestPasswordResetCode(emailFromQuery)
            setShowMessage(true)
        } catch (error) {
            console.error('Error re-requesting code:', error)
        }
    }

    return (
        <React.Fragment>
            <Wrapper>
                <Helmet title={t('pages.auth.resetPasswordWithCode.helmetTitle')} />

                <Typography component='h1' variant='h4' align='center' gutterBottom>
                    {t('pages.auth.resetPasswordWithCode.title')}
                </Typography>

                {showMessage && (
                    <Alert severity='info'>
                        {t('pages.auth.resetPasswordWithCode.infoMessage')}
                        <br />
                        <br />
                        {t('pages.auth.resetPasswordWithCode.infoMessageContactPrefix')}
                        <a href='mailto:info@financelab.se'>info@financelab.se</a>
                        {t('pages.auth.resetPasswordWithCode.infoMessageContactSuffix')}
                    </Alert>
                )}

                {showForm ? (
                    <Formik
                        initialValues={{
                            email: emailFromQuery,
                            code: codeFromQuery,
                            newPassword: '',
                            confirmPassword: '',
                            submit: null,
                        }}
                        validationSchema={Yup.object().shape({
                            email: Yup.string()
                                .email(t('pages.auth.resetPasswordWithCode.validation.emailInvalid'))
                                .required(t('pages.auth.resetPasswordWithCode.validation.emailRequired')),
                            code: Yup.string().required(t('pages.auth.resetPasswordWithCode.validation.codeRequired')),
                            newPassword: Yup.string()
                                .min(10, t('pages.auth.resetPasswordWithCode.validation.passwordMinLength'))
                                .required(t('pages.auth.resetPasswordWithCode.validation.passwordRequired')),
                            confirmPassword: Yup.string()
                                .oneOf(
                                    [Yup.ref('newPassword'), null],
                                    t('pages.auth.resetPasswordWithCode.validation.passwordsMismatch'),
                                )
                                .required(t('pages.auth.resetPasswordWithCode.validation.confirmRequired')),
                        })}
                        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                            try {
                                await userApi.resetPasswordWithCode(values.email, values.code, values.newPassword)
                                setStatus({ success: true })
                                setSubmitting(false)
                                setShowForm(false) // Hide form after successful password reset
                            } catch (error) {
                                const errorMessage =
                                    error?.message || t('pages.auth.resetPasswordWithCode.errors.invalidCode')
                                setStatus({ success: false })
                                setErrors({ submit: errorMessage })
                                setSubmitting(false)
                            }
                        }}
                    >
                        {({
                            errors,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            isSubmitting,
                            touched,
                            values,
                            status,
                        }) => (
                            <form noValidate onSubmit={handleSubmit}>
                                {status && status.success && (
                                    <Alert severity='success'>
                                        {t('pages.auth.resetPasswordWithCode.successMessage')}{' '}
                                        <Button color='inherit' onClick={() => navigate('/auth/sign-in')}>
                                            {t('pages.auth.resetPasswordWithCode.signInLink')}
                                        </Button>
                                        .
                                    </Alert>
                                )}
                                {errors.submit && (
                                    <Alert severity='error'>
                                        {errors.submit}
                                        <Button
                                            color='inherit'
                                            onClick={handleReRequestCode}
                                            style={{ marginLeft: '8px' }}
                                        >
                                            {t('pages.auth.resetPasswordWithCode.requestNewCode')}
                                        </Button>
                                    </Alert>
                                )}

                                <TextField
                                    type='email'
                                    name='email'
                                    label={t('pages.auth.resetPasswordWithCode.fields.email')}
                                    value={values.email}
                                    error={Boolean(touched.email && errors.email)}
                                    helperText={touched.email && errors.email}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={emailRef}
                                />

                                <TextField
                                    type='text'
                                    name='code'
                                    label={t('pages.auth.resetPasswordWithCode.fields.code')}
                                    value={values.code}
                                    error={Boolean(touched.code && errors.code)}
                                    helperText={touched.code && errors.code}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={codeRef}
                                />

                                <TextField
                                    type='password'
                                    name='newPassword'
                                    label={t('pages.auth.resetPasswordWithCode.fields.newPassword')}
                                    value={values.newPassword}
                                    error={Boolean(touched.newPassword && errors.newPassword)}
                                    helperText={touched.newPassword && errors.newPassword}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={newPasswordRef}
                                />

                                <TextField
                                    type='password'
                                    name='confirmPassword'
                                    label={t('pages.auth.resetPasswordWithCode.fields.confirmPassword')}
                                    value={values.confirmPassword}
                                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    margin='normal'
                                    inputRef={confirmPasswordRef}
                                />

                                <Button
                                    type='submit'
                                    fullWidth
                                    variant='contained'
                                    color='primary'
                                    disabled={isSubmitting}
                                    style={{ marginTop: '16px' }}
                                >
                                    {t('pages.auth.resetPasswordWithCode.submit')}
                                </Button>
                            </form>
                        )}
                    </Formik>
                ) : (
                    <Alert severity='success'>
                        {t('pages.auth.resetPasswordWithCode.successMessage')}{' '}
                        <Button color='inherit' onClick={() => navigate('/auth/sign-in')}>
                            {t('pages.auth.resetPasswordWithCode.signInLink')}
                        </Button>
                        .
                    </Alert>
                )}
            </Wrapper>
        </React.Fragment>
    )
}

export default ResetPasswordWithCode
